<template>
  <!-- modal start -->
  <div class="modal fade p-0" id="addClaim" tabindex="-1" aria-labelledby="editLabel" aria-hidden="true">
    <div class="modal-dialog modal-dialog-centered modal-md">
      <div class="d-flex flex-wrap w-100 position-relative event-auto">
        <div class="card shadow-none">
          <Form class="custom-form mt-2" @submit="doSubmit" @keydown="errors_v.clear($event.target.name)" :validation-schema="schema" v-slot="{ errors }">
            <div class="card-header card-header-primary d-flex flex-wrap align-items-center position-relative">
              <div class="content-card-left w-100">
                <h4 class="card-title text-white fs-4">Add Claim / Commission</h4>
                <p class="fw-light text-white mb-0 pt-1">Credit entry that deducts from the company balance</p>
              </div>
            </div>
            <div class="card-body">
              <!-- Category -->
              <div class="position-relative mb-5">
                <label class="mb-1 d-block text-muted small">Category</label>
                <Multiselect v-model="form.category" :options="categoryOptions" class="custom-multiselect"
                  mode="single" :canClear="false" :searchable="false" placeholder="Category" />
                <Field type="hidden" name="category" v-model="form.category" />
                <p v-if="errors.category" class="invalid-message mb-0 pt-2">{{ errors.category }}</p>
              </div>

              <!-- Other (only when category = other) -->
              <div class="position-relative mb-5" v-if="form.category === 'other'">
                <Field
                  type="text"
                  name="category_other"
                  :class="[form.category_other === '' ? '' : 'active', 'border-0 position-relative w-100']"
                  v-model="form.category_other"
                />
                <label for="" class="mb-0 placeholder-label">Specify Category</label>
                <p v-if="errors.category_other" class="invalid-message mb-0 pt-2">{{ errors.category_other }}</p>
              </div>

              <!-- Date -->
              <div class="position-relative mb-5">
                <Field type="date" name="date" :class="[form.date === '' ? '' : 'active', 'border-0 position-relative w-100']" v-model="form.date" />
                <label for="" class="mb-0 placeholder-label">Date</label>
                <p v-if="errors.date" class="invalid-message mb-0 pt-2">{{ errors.date }}</p>
              </div>

              <!-- Amount -->
              <div class="position-relative mb-5">
                <Field
                  type="number"
                  name="amount"
                  :class="[form.amount === '' ? '' : 'active', 'border-0 position-relative w-100']"
                  v-model="form.amount"
                />
                <label for="" class="mb-0 placeholder-label">Amount</label>
                <p v-if="errors.amount" class="invalid-message mb-0 pt-2">{{ errors.amount }}</p>
                <p v-if="errors_v.get('amount')" class="invalid-message mb-0 pt-2">{{ errors_v.get('amount') }}</p>
              </div>

              <!-- Description -->
              <div class="position-relative mb-5">
                <Field
                  type="text"
                  name="description"
                  :class="[form.description === '' ? '' : 'active', 'border-0 position-relative w-100 resize-none']"
                  v-model="form.description"
                />
                <label for="" class="mb-0 placeholder-label">Description (optional)</label>
              </div>
            </div>

            <div class="card-footer px-4 pb-0 border-0 bg-transparent mb-3">
              <button class="btn btn-primary w-100" :disabled="loader">
                <div v-if="loader" class="spinner-border me-2" role="status"></div>Save Entry
              </button>
            </div>
          </Form>
        </div>
      </div>
    </div>
  </div>
  <!-- modal end -->
</template>

<script>
import { ref, reactive } from 'vue';
import Errors from '@/utils/Errors';
import { Form, Field } from 'vee-validate';
import * as Yup from 'yup';
import CompanyBalance from '@/services/CompanyBalanceService';
import Multiselect from '@vueform/multiselect';
import '@vueform/multiselect/themes/default.css';

export default {
  props: {
    company_id: Number,
  },
  components: {
    Form,
    Field,
    Multiselect,
  },
  setup(props, context) {
    const loader = ref(false);

    const form = reactive({
      category: 'claim',
      category_other: '',
      date: new Date().toISOString().slice(0, 10),
      amount: '',
      description: '',
    });

    const schema = Yup.object().shape({
      category: Yup.string().required('Category is required'),
      // Required only when "other" is selected.
      category_other: Yup.string().when('category', {
        is: 'other',
        then: (s) => s.required('Please specify the category'),
        otherwise: (s) => s.notRequired(),
      }),
      amount: Yup.number().typeError('Only numbers allowed').moreThan(0, 'Amount must be greater than zero').required('Amount is required'),
      date: Yup.date().required('Date is required'),
    });

    const errors_v = reactive(new Errors());

    const doSubmit = async (values, actions) => {
      loader.value = true;
      const payload = {
        company_id: props.company_id,
        category: form.category,
        category_other: form.category === 'other' ? form.category_other : null,
        amount: form.amount,
        date: form.date,
        description: form.description || null,
      };
      await CompanyBalance.addCategoryTransaction(payload)
        .then(() => {
          context.emit('load-claims');
          const myModalEl = document.getElementById('addClaim');
          const modal = window.bootstrap.Modal.getInstance(myModalEl);
          modal?.hide();
          loader.value = false;
          actions.resetForm();
          form.category = 'claim';
          form.category_other = '';
          form.amount = '';
          form.description = '';
          form.date = new Date().toISOString().slice(0, 10);
        })
        .catch((error) => {
          loader.value = false;
          if (error?.response?.status === 422) {
            errors_v.record(error.response.data.errors);
          }
        });
    };

    const categoryOptions = [
      { value: 'claim', label: 'Claim' },
      { value: 'commission', label: 'Commission' },
      { value: 'salary', label: 'Salary' },
      { value: 'other', label: 'Other' },
    ];

    return {
      schema,
      form,
      categoryOptions,
      doSubmit,
      errors_v,
      loader,
    };
  },
};
</script>
